// useAnnouncements — the hand-written per-domain hooks over the generated client for the
// Antenna announcements composer. Lives under app/hooks/ (the lint-exempt wrapper dir), so feature
// screens use THESE and never raw useQuery/useMutation. Built on useBandQuery/useBandMutation, so every key is
//   ['band', bandId, 'antenna', …]
// and a module can never fetch outside its tenant.
//
// Mutation posture: create/edit/publish/retract are ALL confirm-then-repaint — NEVER optimistic.
// publish + retract are the money/publish-class writes explicitly barred from the optimistic set: an explicit
// pending state, server confirmation, the server error surfaced verbatim, and NO success on an unpersisted
// write (a non-2xx or a body without the row throws → the mutation never resolves "success").
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError } from "./apiError.js";

type AnnouncementSummaryPaged = components["schemas"]["AnnouncementSummaryPaged"];
type AnnouncementSummary = components["schemas"]["AnnouncementSummary"];
type AnnouncementView = components["schemas"]["AnnouncementView"];
type FanClubSettingsView = components["schemas"]["FanClubSettingsView"];
type FanClubOverview = components["schemas"]["FanClubOverview"];
type CreateAnnouncementRequest = components["schemas"]["CreateAnnouncementRequest"];
type UpdateAnnouncementRequest = components["schemas"]["UpdateAnnouncementRequest"];

// ---- query keys (full keys, for direct cache access) --------------------------------------------------

/** The announcements-list key. fetches the full list (no status filter) so hub + edit share one cache.*/
export const announcementsKey = (bandId: string) =>
  ["band", bandId, "antenna", "announcements"] as const;

/** The fan-club settings key (the audience tiers the composer reads). */
export const settingsKey = (bandId: string) => ["band", bandId, "antenna", "settings"] as const;

/** The hub overview / KPI key (the band-metrics-sourced shot-37 numbers, A4). */
export const overviewKey = (bandId: string) => ["band", bandId, "antenna", "overview"] as const;

/**
 * A single announcement's FULL view key. There is no GET-single route (an decision), so this cache is
 * populated only by create/edit/publish responses — the edit modal reads it to re-hydrate a draft with full
 * fidelity (body/cities), falling back to the list summary otherwise.
 */
export const announcementKey = (bandId: string, id: string) =>
  ["band", bandId, "antenna", "announcement", id] as const;

// ---- queries ------------------------------------------------------------------------------------------

/**
 * GET the band's announcements. `antenna:read`; a Content seat / unassigned org 403s → the query's
 * error state (the client mirror of the server deny). `seenCount`/`clickCount` come back null (A4 degrade).
 */
export function useAnnouncements() {
  const { api } = useApp();
  return useBandQuery<AnnouncementSummaryPaged>(
    ["antenna", "announcements"],
    async (bandId) => {
      const { data, error, response } = await api.GET(
        "/api/v1/bands/{bandId}/antenna/announcements",
        { params: { path: { bandId } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 15_000 },
  );
}

/**
 * GET the fan-club settings — the audience tiers the composer's selector renders. `antenna:manage`
 * (only a composer-opener reads it). Born-blank returns the locked free tier + version 0 without persisting.
 */
export function useFanClubSettings() {
  const { api } = useApp();
  return useBandQuery<FanClubSettingsView>(
    ["antenna", "settings"],
    async (bandId) => {
      const { data, error, response } = await api.GET(
        "/api/v1/bands/{bandId}/antenna/settings",
        { params: { path: { bandId } } },
      );
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { staleTime: 300_000 }, // settings rarely change (directory-class freshness)
  );
}

/**
 * GET the fan-club hub KPIs (A4) — the shot-37 numbers, sourced by core from the Antenna internal
 * band-metrics API (the band's OWN club only). `antenna:read`. When Antenna is unavailable the
 * whole shape degrades to the empty card (zeros / null seen-rate / no tiers) — the query still resolves
 * (a degraded 200), so the strip renders born-blank, never an error banner.
 */
export function useFanClubOverview() {
  const { api } = useApp();
  return useBandQuery<FanClubOverview>(
    ["antenna", "overview"],
    async (bandId) => {
      const { data, error, response } = await api.GET(
        "/api/v1/bands/{bandId}/antenna/overview",
        { params: { path: { bandId } } },
      );
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { staleTime: 30_000 }, // KPIs are always-fresh on the server; a short client cache smooths tab flips
  );
}

// ---- mutations (all confirm-then-repaint) -------------------------------------------------------------

/** Create a draft (POST → 201). Not optimistic. Caches the full view so a follow-on edit is faithful.*/
export function useCreateAnnouncement() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<AnnouncementView, CreateAnnouncementRequest>(
    async (band, body) => {
      const { data, error, response } = await api.POST(
        "/api/v1/bands/{bandId}/antenna/announcements",
        { params: { path: { bandId: band } }, body },
      );
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onSuccess: (view) => {
        if (view.announcementId) qc.setQueryData(announcementKey(bandId, view.announcementId), view);
        void qc.invalidateQueries({ queryKey: announcementsKey(bandId) });
      },
    },
  );
}

/** Edit a DRAFT (PUT → 200; a published/retracted row is 409 `invalid-state`). Not optimistic.*/
export function useUpdateAnnouncement() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<AnnouncementView, { id: string; body: UpdateAnnouncementRequest }>(
    async (band, { id, body }) => {
      const { data, error, response } = await api.PUT(
        "/api/v1/bands/{bandId}/antenna/announcements/{id}",
        { params: { path: { bandId: band, id } }, body },
      );
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onSuccess: (view) => {
        if (view.announcementId) qc.setQueryData(announcementKey(bandId, view.announcementId), view);
        void qc.invalidateQueries({ queryKey: announcementsKey(bandId) });
      },
    },
  );
}

/**
 * Publish a draft (POST → 202) — NEVER optimistic. The caller passes an `idempotencyKey`
 * a fresh uuid per publish INTENT that is REUSED across retries so a network-blip retry collapses
 * to one publish (the backend replays the stored 202 with `Idempotency-Replayed: true`). A 409 `invalid-state`
 * (no longer a draft) surfaces verbatim — the caller must NOT retry it.
 */
export function usePublishAnnouncement() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<AnnouncementView, { id: string; idempotencyKey: string }>(
    async (band, { id, idempotencyKey }) => {
      const { data, error, response } = await api.POST(
        "/api/v1/bands/{bandId}/antenna/announcements/{id}/publish",
        {
          params: { path: { bandId: band, id } },
          headers: { "Idempotency-Key": idempotencyKey },
        },
      );
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onSuccess: (view) => {
        if (view.announcementId) qc.setQueryData(announcementKey(bandId, view.announcementId), view);
        void qc.invalidateQueries({ queryKey: announcementsKey(bandId) });
      },
    },
  );
}

/**
 * Retract an announcement (DELETE → 204) — NEVER optimistic. A published row emits `…deleted`; a draft
 * emits nothing; an already-retracted row is an idempotent 204. Confirm-then-repaint: the list invalidates on
 * server success, and the error surfaces verbatim (no phantom "retracted").
 */
export function useRetractAnnouncement() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<void, string>(
    async (band, id) => {
      const { error, response } = await api.DELETE(
        "/api/v1/bands/{bandId}/antenna/announcements/{id}",
        { params: { path: { bandId: band, id } } },
      );
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onSuccess: (_data, id) => {
        void qc.invalidateQueries({ queryKey: announcementKey(bandId, id) });
        void qc.invalidateQueries({ queryKey: announcementsKey(bandId) });
      },
    },
  );
}

// ---- edit hydration (no GET-single) -------------------------------------------------------------------

/** What the edit modal can reconstruct for a draft without a GET-single route. */
export interface AnnouncementForEdit {
  /** The full view (body/cities included) — present only when this session created/edited the draft. */
  view?: AnnouncementView;
  /** The list summary (type/title/audienceTier) — the fallback when no full view is cached. */
  summary?: AnnouncementSummary;
  /** True once we hold the full body (from `view`); false ⇒ the modal must guard against a blank-body save. */
  hasBody: boolean;
}

/**
 * Reconstruct a draft for the edit modal (has no GET-single — an decision). Reads the full view
 * from cache when available (created/edited this session ⇒ faithful body/cities), else the list summary. The
 * modal treats `hasBody === false` as "the message wasn't loaded" and refuses an unedited save so it can never
 * silently blank an existing body.
 */
export function useAnnouncementForEdit(id: string): AnnouncementForEdit {
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  const view = qc.getQueryData<AnnouncementView>(announcementKey(bandId, id));
  const list = qc.getQueryData<AnnouncementSummaryPaged>(announcementsKey(bandId));
  const summary = list?.items?.find((row) => row.announcementId === id);
  return { view, summary, hasBody: view != null };
}
